/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react-hooks/rules-of-hooks */
import React, { lazy, useState, useRef, Suspense } from 'react';
import PropTypes from 'prop-types';
import { FaArrowRight } from '@react-icons/all-files/fa/FaArrowRight';
import LazyImage from 'gatsby-core-theme/src/hooks/lazy-image';
import { imagePrettyUrl } from 'gatsby-core-theme/src/helpers/getters';
import getField from 'gatsby-core-theme/src/components/organisms/form/fields';
import { contactUsForm } from 'gatsby-core-theme/src/constants/forms';
import useTranslate from '~hooks/useTranslate/useTranslate';

import newsletterStyles from './form.module.scss';
import dynamicNewsletterStyles from './dynamic-form.module.scss';

const ReCAPTCHA = lazy(() => import('react-google-recaptcha'));

const FormComponent = ({
  formOptions = Object.values(contactUsForm)[0] || {},
  successMessage = 'Form has sent successfully',
  failMessage = 'Form has not sent, Got some error',
  validationMessage = 'Fill all the required fields.',
  submitUrl = '',
  hasButton = true,
  showButtonIcon = true,
  buttonLabel = 'Submit',
  disabled = true,
  showLabels = true,
  customeSubmit = null,
  customError = false,
  path = '',
  titleType = 'h2',
  buttonIcon = <FaArrowRight fontSize={20} title="Right-pointing Arrow Icon" />,
  dynamic = false,
  page,
}) => {
  const styles = dynamic ? dynamicNewsletterStyles : newsletterStyles;
  const color = page?.extra_fields?.operator_background_color;
  const formImage = page?.extra_fields?.newsletter_form_image;

  const recaptchaRef = useRef();
  const formRef = useRef();
  const [state, setState] = useState({
    loading: false,
    success: false,
    failed: false,
    isValid: true,
    isDisabled: disabled,
  });
  const values =
    formOptions.fields?.map((field) => ({
      [field.id]: field.value ? field.value : '',
    })) || [];
  // add recaptcha if its added in form options
  if (formOptions.hasReCAPTCHA) values.push({ gRecaptchaResponse: '' });
  const [elements, setElements] = useState(Object.assign({}, ...values));

  const handleChange = (name, value) => {
    setState({ ...state, [name]: value });
    setElements({ ...elements, [name]: value });
  };

  async function postData(url = '', data = {}) {
    return fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(Object.fromEntries(data)),
    });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isEmpty = (value) => value === '';
    const form = e.target;

    // Check if all required fields are empty
    const isValid = !Object.values(elements).some((el) => el.required && isEmpty(el));
    setState({ ...state, loading: true, success: false, failed: false, isValid, isDisabled: true });

    try {
      let response;
      if (customeSubmit) {
        response = await customeSubmit(form);
      } else {
        response = await postData(submitUrl, new FormData(form));
      }
      setState({
        ...state,
        success: response === true,
        failed: response === false,
        loading: false,
        isValid: true,
        isDisabled: true,
      });
    } catch (err) {
      // Error handling
      setState({
        ...state,
        failed: true,
        loading: false,
        isValid: true,
      });
    }

    recaptchaRef?.current?.reset();
  };

  function recaptchaOnChange(value) {
    // eslint-disable-next-line no-unused-expressions
    if (value) {
      setState({ ...state, isDisabled: false });
      setElements({ ...elements, gRecaptchaResponse: value });
    }
  }

  // form not specified on constant
  if (!Object.keys(formOptions).length) return;

  const TitleTag = titleType || 'h2';

  return (
    <div className={styles.formOuter}>
      {dynamic && formImage && (
        <LazyImage
          alt="dynamic newsletter iamge"
          loading="lazy"
          width={469}
          height={504}
          className={styles.newletterImage}
          src={imagePrettyUrl(formImage, 469, 504)}
        />
      )}
      <div className={`${styles.formComponent || ''} `} style={color ? { background: color } : {}}>
        {formOptions?.title && (
          <TitleTag className={styles.title}>
            {useTranslate(
              [`${path}_${formOptions?.title?.translationKey}`, formOptions.title?.translationKey],
              formOptions.title?.label
            )}
          </TitleTag>
        )}
        {formOptions?.description && (
          <p>
            {useTranslate(
              [
                `${path}_${formOptions?.description?.translationKey}`,
                formOptions?.description?.translationKey,
              ],
              formOptions.description?.label
            )}
          </p>
        )}
        {formOptions?.fields && (
          <form
            ref={formRef}
            onSubmit={(e) => handleSubmit(e)}
            action={submitUrl}
            className={formOptions.twoCol ? styles.twoCol || '' : styles.singleCol || ''}
            target="_self"
          >
            {formOptions.fields?.map((field) => {
              const { id, translationKey, label, validations } = field;

              return (
                <div
                  key={id}
                  className={`${styles.formGroup || ''} ${(dynamic && styles.dynamicForm) || ''} ${
                    styles[id] ? styles[id] : ''
                  } ${(!field.twoCol && styles.fullWidth) || ''}`}
                >
                  {showLabels && label && (
                    <label htmlFor={id}>{useTranslate(translationKey, label)}</label>
                  )}
                  {elements && getField(field, handleChange, elements, state)}
                  {(field?.type === 'text' || field?.type === 'email') && dynamic && (
                    <LazyImage
                      alt={`dynamic_newsletter_${field.id.toLowerCase()}`}
                      loading="lazy"
                      width={24}
                      height={24}
                      className={styles.inputImage}
                      src={
                        `${page.extra_fields?.[`dynamic_newsletter_${field.id.toLowerCase()}`]}` !==
                        'undefined'
                          ? imagePrettyUrl(
                              `${
                                page.extra_fields?.[`dynamic_newsletter_${field.id.toLowerCase()}`]
                              }`,
                              24,
                              24
                            )
                          : `/images/${field?.id}-icon.svg`
                      }
                    />
                  )}

                  {validations && !state.isValid && elements[id] === '' && (
                    <span className={styles.alertWarningMessage || ''}>
                      {validations?.icon}
                      {useTranslate(validations?.translationKey, validations?.label)}
                    </span>
                  )}
                </div>
              );
            })}
            {dynamic && (
              <span
                className={styles.disclaimer}
                dangerouslySetInnerHTML={{
                  __html: useTranslate(
                    `${page.path}_dynamic_newsletter_disclaimer`,
                    'By signing up I confirm I am over 19 years old and I agree that my contact data may be used to keep me informed about casino products, services, and offerings.  Read the Privacy Policy for more details. You can unsubscribe from receiving future marketing communication at any time.'
                  ),
                }}
              />
            )}
            {formOptions.hasReCAPTCHA && (
              <Suspense fallback={<div>Loading ReCAPTCHA...</div>}>
                <div
                  className={`${styles.recaptcha || ''} ${
                    (!state.isValid && elements.gRecaptchaResponse === '' && styles.invalid) || ''
                  }`}
                >
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={`${process.env.RECAPTCHA_SITE_KEY}`}
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={recaptchaOnChange}
                  />
                  {!state.isValid &&
                    elements.gRecaptchaResponse === '' &&
                    formOptions?.reCaptcha?.validations && (
                      <span className={styles.alertWarningMessage || ''}>
                        {formOptions?.reCaptcha?.validations?.icon}
                        {useTranslate(
                          formOptions?.reCaptcha?.validations?.translationKey,
                          formOptions?.reCaptcha?.validations.label
                        )}
                      </span>
                    )}
                </div>
              </Suspense>
            )}
            {hasButton && (
              <button disabled={state.loading} className={styles.formButton || ''} type="submit">
                {state.loading
                  ? useTranslate('loading_btn', 'sending...')
                  : useTranslate('send_button', buttonLabel)}
                {showButtonIcon ? buttonIcon : null}
              </button>
            )}
            {!customError && (state.success || state.failed || !state.isValid) && (
              <div className={styles.formAlerts || ''}>
                {state.success && (
                  <div className={styles.alertSuccess || ''}>
                    {useTranslate(
                      `${dynamic ? `${page.path}_dynamic_success_message` : `success_message`}`,
                      successMessage
                    )}
                    {dynamic && (
                      <span>
                        {useTranslate(
                          `${
                            dynamic
                              ? `${page.path}_dynamic_success_description`
                              : `success_description`
                          }`,
                          ''
                        )}
                      </span>
                    )}
                  </div>
                )}
                {state.failed && (
                  <div className={styles.alertDanger || ''}>
                    {useTranslate(
                      `${dynamic ? `${page.path}_dynamic_fail_message` : `fail_message`}`,
                      failMessage
                    )}
                  </div>
                )}
                {!state.isValid && (
                  <div className={styles.alertWarning || ''}>
                    {useTranslate(
                      `${
                        dynamic
                          ? `${page.path}_dynamic_validation_message`
                          : `success_validation_message`
                      }`,
                      validationMessage
                    )}
                  </div>
                )}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

FormComponent.propTypes = {
  formOptions: PropTypes.shape({}),
  successMessage: PropTypes.string,
  failMessage: PropTypes.string,
  validationMessage: PropTypes.string,
  submitUrl: PropTypes.string,
  hasButton: PropTypes.bool,
  buttonLabel: PropTypes.string,
  disabled: PropTypes.bool,
  showLabels: PropTypes.bool,
  showButtonIcon: PropTypes.bool,
  customError: PropTypes.bool,
  customeSubmit: PropTypes.func,
  path: PropTypes.string,
  dynamic: PropTypes.bool,
  titleType: PropTypes.string,
  buttonIcon: PropTypes.element,
  page: PropTypes.shape({
    path: PropTypes.string,
    extra_fields: PropTypes.shape({
      operator_background_color: PropTypes.string,
      newsletter_form_image: PropTypes.string,
    }),
  }),
};
export default FormComponent;
